import { Box, Grid, Typography } from "@mui/material";
import {
  AccountCircleOutlined,
  StairsOutlined,
  HistoryEduOutlined,
  AccessTimeOutlined,
  AttachFile,
  PersonOutlineOutlined,
  InfoOutlined,
  EventOutlined,
  Numbers,
  LocationOnOutlined,
  AccountBalanceOutlined,
} from "@mui/icons-material";
import ReactHtmlParser from "html-react-parser";

import { useTicketDetailContext } from "contexts/TicketDetailContext";

import InformationTile from "components/Shared/InformationTile";
import FileIcon from "components/Shared/FileIcon";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";

import { useAuth } from "hooks";

const TicketBasicDetail = () => {
  const { isAdmin } = useAuth();

  const { ticketDetail, loadingTicket } = useTicketDetailContext();

  return !loadingTicket ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Typography variant="h6">
            <span className="text-xl text-blue-500 dark:text-blue-200">
              #{ticketDetail?.ticketId}
            </span>{" "}
            {ticketDetail?.subject}
          </Typography>
        </Grid>

        {isAdmin && (
          <>
            <Grid item md={3}>
              <InformationTile
                icon={() => <Numbers color="primary" />}
                title="Ticket No"
                subtitle={ticketDetail?.ticketNo}
              />
            </Grid>
            <Grid item md={3}>
              <InformationTile
                icon={() => <PersonOutlineOutlined color="primary" />}
                title={
                  ticketDetail?.source === "Customer"
                    ? "Customer Profile"
                    : ticketDetail?.source
                      ? ticketDetail.source.replace(/([a-z])([A-Z])/g, "$1 $2")
                      : "Source"
                }
              >
                {ticketDetail?.sourceRef ? (
                  ticketDetail?.source === "Customer" ? (
                    <OpenInNewTab
                      label={
                        ticketDetail?.sourceRef?.name ??
                        ticketDetail?.sourceRef?.customerUser?.name
                      }
                      link={`/app/crm/customerProfile/${ticketDetail?.sourceRef?._id}/detail`}
                    />
                  ) : (
                    <OpenInNewTab
                      label={ticketDetail?.sourceRef?.shopName}
                      link={`/app/crm/vendor-profile/${ticketDetail?.sourceRef?._id}/detail`}
                    />
                  )
                ) : (
                  "N/A"
                )}
              </InformationTile>
            </Grid>

            <Grid item md={3}>
              <InformationTile
                icon={() => <HistoryEduOutlined color="primary" />}
                title="Requested By"
                subtitle={ticketDetail?.createdBy?.name}
              />
            </Grid>

            <Grid item md={3}>
              <InformationTile
                icon={() => <AccountCircleOutlined color="primary" />}
                title="Responsible"
                subtitle={ticketDetail?.responsible?.name}
              />
            </Grid>
            <Grid item md={3}>
              <InformationTile
                icon={() => <AccountBalanceOutlined color="primary" />}
                title="Ticket Type"
                subtitle={ticketDetail?.ticketType?.name}
              />
            </Grid>

            <Grid item md={3}>
              <InformationTile
                icon={() => <InfoOutlined color="primary" />}
                title="Status"
              >
                <Status isActive={ticketDetail?.isActive} />
              </InformationTile>
            </Grid>
          </>
        )}

        <Grid item md={3}>
          <InformationTile
            icon={() => <StairsOutlined color="primary" />}
            title="Stage"
            subtitle={ticketDetail?.stage}
          />
        </Grid>

        <Grid item md={3}>
          <InformationTile
            icon={() => <AccessTimeOutlined color="primary" />}
            title="Created At"
            subtitle={new Date(ticketDetail?.createdAt).toLocaleDateString()}
          />
        </Grid>

        <Grid item md={3}>
          <InformationTile
            icon={() => <EventOutlined color="error" />}
            title="Due Date"
            subtitle={
              new Date(ticketDetail?.dueDate).toLocaleDateString() ?? "N/A"
            }
          />
        </Grid>

        {ticketDetail?.stage === "Closed" && ticketDetail?.closedAt && (
          <Grid item md={3}>
            <InformationTile
              icon={() => <AccessTimeOutlined color="error" />}
              title="Closed At"
              subtitle={`${new Date(
                ticketDetail?.closedAt
              ).toLocaleDateString()} by ${ticketDetail?.closedBy?.name}`}
            />
          </Grid>
        )}

        {!!ticketDetail?.attachments?.length && (
          <Grid item md={12}>
            <InformationTile
              icon={() => <AttachFile color="primary" />}
              title="Attachments"
            >
              <div className="flex gap-4">
                {ticketDetail?.attachments?.map((attachment) => (
                  <a
                    key={attachment?._id}
                    href={attachment?.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--primary)" }}
                  >
                    <FileIcon
                      fileName={attachment?.name}
                      fileUrl={attachment?.url}
                    />
                  </a>
                ))}
              </div>
            </InformationTile>
          </Grid>
        )}
      </Grid>

      <br />
      {ticketDetail?.bankDetails?.bankName && (
        <Grid item md={12}>
          <InformationTile
            icon={() => <AccountBalanceOutlined color="primary" />}
            title="Bank Details"
          >
            <div className="flex flex-col gap-1">
              <Typography variant="body2">
                <strong>Bank Name:</strong> {ticketDetail.bankDetails.bankName}
              </Typography>
              <Typography variant="body2">
                <strong>Account Name:</strong> {ticketDetail.bankDetails.bankAccountName}
              </Typography>
              <Typography variant="body2">
                <strong>Account Number:</strong> {ticketDetail.bankDetails.bankAccountNumber}
              </Typography>
            </div>
          </InformationTile>
        </Grid>
      )}

      {ticketDetail?.address && (
        <Grid item md={6}>
          <InformationTile
            icon={() => <LocationOnOutlined color="primary" />}
            title="Address"
            subtitle={ticketDetail.address}
          />
        </Grid>
      )}
      <br />

      {ticketDetail?.description && (
        <Box>{ReactHtmlParser(ticketDetail?.description)}</Box>
      )}
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default TicketBasicDetail;
